import { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, Dumbbell, Plus, X } from 'lucide-react';
import { Button } from '../ui/button';
import { useBackHandler } from '../../lib/backNav';

const fmtDate = (d) => new Date(d + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
const fmtSet = (s) => `${s.weight}×${s.reps}${s.rpe ? ` @${s.rpe}` : ''}`;
const todayStr = () => {
  const n = new Date();
  return `${n.getFullYear()}-${String(n.getMonth() + 1).padStart(2, '0')}-${String(n.getDate()).padStart(2, '0')}`;
};
const emptyRow = () => ({ weight: '', reps: '', rpe: '' });
const inputCls = 'h-10 w-full rounded-md border border-input bg-background text-center text-base font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring';
const CATEGORIES = ['Chest', 'Back', 'Shoulders', 'Biceps', 'Triceps', 'Legs', 'Core', 'Olympic', 'Cardio', 'Warmup', 'Mobility', 'Uncategorized'];

const ExerciseDetail = ({ api, token, user, exercise, onBack, onUpdated }) => {
  useBackHandler(true, onBack);
  const isAdmin = user?.role === 'admin';
  const [category, setCategory] = useState(exercise.muscle_group);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [logging, setLogging] = useState(false);
  const [rows, setRows] = useState([emptyRow()]);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/exercises/${exercise.id}/history`, token)
      .then(setData)
      .catch(() => setData({ stats: {}, sessions: [] }))
      .finally(() => setLoading(false));
  }, [api, token, exercise.id]);
  useEffect(() => { load(); }, [load]);

  const updateRow = (i, k, v) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  const removeRow = (i) => setRows((rs) => (rs.length > 1 ? rs.filter((_, j) => j !== i) : rs));

  const saveLog = async () => {
    const sets = rows.filter((r) => r.weight !== '' || r.reps !== '');
    if (!sets.length) return;
    setSaving(true);
    try {
      await api.post(`/exercises/${exercise.id}/log`, { date: todayStr(), sets }, token);
      setLogging(false); setRows([emptyRow()]);
      load();
    } catch { /* keep panel open */ } finally { setSaving(false); }
  };

  // Admin: change the exercise's category (locks it against re-ingest reclassify).
  const changeCategory = async (next) => {
    const prev = category;
    setCategory(next); // optimistic
    try {
      await api.put(`/exercises/${exercise.id}`, { muscle_group: next }, token);
      onUpdated && onUpdated();
    } catch { setCategory(prev); }
  };

  const prs = data?.prs || {};

  return (
    <div className="max-w-2xl mx-auto p-4">
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-3">
        <ChevronLeft className="h-4 w-4" /> Library
      </button>

      <h2 className="font-display font-bold text-2xl leading-tight">{exercise.name}</h2>
      {isAdmin ? (
        <div className="flex items-center gap-2 mb-3 mt-1">
          <span className="text-xs text-muted-foreground">Category</span>
          <select value={category} onChange={(e) => changeCategory(e.target.value)}
            className="h-8 rounded-md border border-input bg-background px-2 text-sm text-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
            {[...new Set([...CATEGORIES, category])].map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      ) : (
        <div className="text-sm text-primary mb-3">{category}</div>
      )}

      {exercise.youtube_video_id ? (
        <div className="aspect-video bg-black rounded-lg overflow-hidden mb-5">
          <iframe
            className="w-full h-full"
            src={`https://www.youtube.com/embed/${exercise.youtube_video_id}?rel=0`}
            title={exercise.name}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      ) : (
        <div className="aspect-video bg-muted rounded-lg mb-5 flex flex-col items-center justify-center text-muted-foreground/50 text-sm">
          <Dumbbell className="h-8 w-8 mb-1" />
          No video yet
        </div>
      )}

      {/* Quick log — record a set outside any program (assessment / PR attempt) */}
      {!logging ? (
        <Button className="w-full h-11 mb-5" onClick={() => setLogging(true)}>
          <Plus className="h-4 w-4 mr-1" /> Log a set
        </Button>
      ) : (
        <div className="rounded-lg border bg-card p-3 mb-5">
          <div className="flex items-center justify-between mb-2">
            <span className="font-semibold text-sm">Log sets · today</span>
            <button onClick={() => { setLogging(false); setRows([emptyRow()]); }} aria-label="Close"><X className="h-5 w-5 text-muted-foreground" /></button>
          </div>
          <div className="grid grid-cols-[2rem_1fr_1fr_1fr_auto] gap-2 items-center text-xs text-muted-foreground px-1 mb-1">
            <span>Set</span><span>Weight</span><span>Reps</span><span>RPE</span><span></span>
          </div>
          <div className="space-y-2">
            {rows.map((r, i) => (
              <div key={i} className="grid grid-cols-[2rem_1fr_1fr_1fr_auto] gap-2 items-center">
                <span className="text-center text-sm font-semibold text-muted-foreground">{i + 1}</span>
                <input type="number" inputMode="decimal" value={r.weight} onChange={(e) => updateRow(i, 'weight', e.target.value)} placeholder="lb" className={inputCls} />
                <input type="number" inputMode="numeric" value={r.reps} onChange={(e) => updateRow(i, 'reps', e.target.value)} placeholder="reps" className={inputCls} />
                <input type="number" inputMode="numeric" value={r.rpe} onChange={(e) => updateRow(i, 'rpe', e.target.value)} placeholder="–" className={inputCls} />
                <button onClick={() => removeRow(i)} className="h-10 w-9 rounded-md border flex items-center justify-center text-muted-foreground hover:text-destructive" aria-label="Remove set"><X className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
          <button onClick={() => setRows((rs) => [...rs, emptyRow()])} className="flex items-center gap-1 text-sm text-primary hover:underline px-1 pt-2">
            <Plus className="h-4 w-4" /> Add set
          </button>
          <Button className="w-full h-11 mt-3" onClick={saveLog} disabled={saving}>{saving ? 'Saving…' : 'Save to history'}</Button>
          <p className="text-xs text-muted-foreground mt-2">Saved as today's workout for this exercise — it counts toward your PRs and future suggested weights.</p>
        </div>
      )}

      {loading ? (
        <p className="text-muted-foreground text-sm py-8 text-center">Loading…</p>
      ) : (
        <>
          {/* Personal records */}
          <h3 className="font-semibold mb-2">Personal records</h3>
          <div className="grid grid-cols-2 gap-2 mb-6">
            <PRCard label="Est. 1RM" stat={prs.e1rm}
              value={(s) => `${s.value} lb`} sub={(s) => `${s.detail} · ${fmtDate(s.date)}`} />
            <PRCard label="Max weight" stat={prs.max_weight}
              value={(s) => `${s.value} lb`} sub={(s) => `${s.detail} · ${fmtDate(s.date)}`} />
            <PRCard label="10-rep max" stat={prs.rep10}
              value={(s) => `${s.value} lb`} sub={(s) => `${s.detail} · ${fmtDate(s.date)}`} />
            <PRCard label="Session volume" stat={prs.session_volume}
              value={(s) => `${s.value.toLocaleString()} lb`} sub={(s) => fmtDate(s.date)} />
          </div>

          {/* History */}
          <h3 className="font-semibold mb-2">History</h3>
          {(!data.sessions || data.sessions.length === 0) ? (
            <p className="text-sm text-muted-foreground">No sets logged yet — log one above or do this exercise in a workout and it'll show up here.</p>
          ) : (
            <div className="space-y-2">
              {data.sessions.map((s, i) => (
                <div key={i} className="rounded-lg border bg-card p-3">
                  <div className="text-sm font-medium mb-2">{fmtDate(s.date)}</div>
                  <div className="space-y-1">
                    {s.sets.map((set, j) => (
                      <div key={j} className="flex justify-between text-sm">
                        <span className="text-muted-foreground">Set {j + 1}</span>
                        <span className="font-medium">{fmtSet(set)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

const PRCard = ({ label, stat, value, sub }) => (
  <div className="rounded-lg border bg-card p-3">
    <div className="text-xs text-muted-foreground">{label}</div>
    {stat ? (
      <>
        <div className="text-lg font-bold leading-tight mt-0.5">{value(stat)}</div>
        <div className="text-[11px] text-muted-foreground mt-0.5 leading-tight">{sub(stat)}</div>
      </>
    ) : (
      <div className="text-lg font-bold text-muted-foreground/40 mt-0.5">—</div>
    )}
  </div>
);

export default ExerciseDetail;
